import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FileText, Send, Edit, XCircle, CheckCircle, Clock,
  Eye, Building2, IndianRupee, RotateCcw, Receipt,
} from "lucide-react";
import { AppButton, AppBadge, BackButton, PageHeader, DetailRow } from "../../ui";
import CommonToaster from "../../Common/CommonToaster";
import apiClient from "../../../utils/apiClient";
import "./service-invoices.scss";

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount || 0);
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

const STATUS_CONFIG = {
  draft: { label: "Draft", variant: "secondary" },
  sent: { label: "Sent", variant: "info" },
  viewed: { label: "Viewed", variant: "primary" },
  pending_payment: { label: "Pending Payment", variant: "warning" },
  paid: { label: "Paid", variant: "success" },
  overdue: { label: "Overdue", variant: "danger" },
  cancelled: { label: "Cancelled", variant: "secondary" },
};

const SERVICE_TYPE_LABELS = {
  custom_development: "Custom Development",
  integration: "Integration",
  support: "Support",
  amc: "AMC",
  training: "Training",
  revenue_share: "Revenue Share",
  others: "Others",
};

const ServiceInvoiceDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [revShareLines, setRevShareLines] = useState([]);
  const [revShareLoading, setRevShareLoading] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [issuingCN, setIssuingCN] = useState(false);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.get(`/sa/service-invoices/${id}`);
      setInvoice(data.invoice);
    } catch {
      setToast({ show: true, message: "Failed to load invoice.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInvoice(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (invoice?.serviceType === "revenue_share") {
      (async () => {
        try {
          setRevShareLoading(true);
          const { data } = await apiClient.get(`/sa/revenue-share/invoices/${id}/lines`, { params: { limit: 500 } });
          setRevShareLines(data.lines || []);
        } catch {
          setToast({ show: true, message: "Failed to load revenue-share lines.", type: "error" });
        } finally {
          setRevShareLoading(false);
        }
      })();
    }
  }, [invoice?.serviceType, id]);

  const handleVoidRevShare = async () => {
    if (!window.confirm("Void this revenue-share invoice and re-bill students next month?")) return;
    try {
      setVoiding(true);
      await apiClient.post(`/sa/revenue-share/invoices/${id}/void`);
      setToast({ show: true, message: "Invoice voided.", type: "success" });
      fetchInvoice();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to void.", type: "error" });
    } finally {
      setVoiding(false);
    }
  };

  const handleIssueCreditNote = async () => {
    const reason = window.prompt("Reason for credit note:");
    if (!reason) return;
    const amountStr = window.prompt(`Credit note amount in INR (max ${invoice.totalAmount})?`, String(invoice.totalAmount));
    const amount = Number(amountStr);
    if (!amount || amount <= 0) return;
    try {
      setIssuingCN(true);
      const { data } = await apiClient.post(`/sa/revenue-share/invoices/${id}/credit-note`, { reason, amount });
      setToast({ show: true, message: `Credit note ${data.invoice?.invoiceNumber} created.`, type: "success" });
      fetchInvoice();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to issue credit note.", type: "error" });
    } finally {
      setIssuingCN(false);
    }
  };

  const handleSend = async () => {
    try {
      await apiClient.patch(`/sa/service-invoices/${id}/send`);
      setToast({ show: true, message: "Invoice sent.", type: "success" });
      fetchInvoice();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed.", type: "error" });
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Cancel this invoice?")) return;
    try {
      await apiClient.patch(`/sa/service-invoices/${id}/cancel`);
      setToast({ show: true, message: "Invoice cancelled.", type: "success" });
      fetchInvoice();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed.", type: "error" });
    }
  };

  const handleApprove = async () => {
    try {
      await apiClient.patch(`/sa/service-invoices/${id}/approve-payment`);
      setToast({ show: true, message: "Payment approved.", type: "success" });
      fetchInvoice();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed.", type: "error" });
    }
  };

  const handleReject = async () => {
    const reason = window.prompt("Reason for rejection (optional):");
    if (reason === null) return;
    try {
      await apiClient.patch(`/sa/service-invoices/${id}/reject-payment`, { reason });
      setToast({ show: true, message: "Payment rejected.", type: "success" });
      fetchInvoice();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed.", type: "error" });
    }
  };

  const handlePreviewPdf = async () => {
    try {
      const response = await apiClient.get(`/sa/service-invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      setToast({ show: true, message: "Failed to generate PDF.", type: "error" });
    }
  };

  if (loading) {
    return <div className="si-page"><div className="si-loading">Loading...</div></div>;
  }

  if (!invoice) {
    return <div className="si-page"><div className="si-loading">Invoice not found.</div></div>;
  }

  const sc = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.draft;
  const inst = invoice.instituteId || {};
  const billingFrom = invoice.billingFrom || {};
  const billingTo = invoice.billingTo || {};
  const hasTax = (invoice.taxRate || 0) > 0;
  const isIntra = (invoice.cgstRate || 0) > 0;
  const isInter = (invoice.igstRate || 0) > 0;

  return (
    <div className="si-page">
      <PageHeader
        icon={FileText}
        title={`Invoice ${invoice.invoiceNumber}`}
        description={`Version ${invoice.version} • Created ${formatDate(invoice.createdAt)}`}
        actions={<BackButton />}
      />

      {/* Status & Actions Bar */}
      <div className="si-detail-actions-bar">
        <AppBadge variant={sc.variant}>{sc.label}</AppBadge>
        <div className="si-detail-actions">
          <AppButton variant="outline" size="sm" onClick={handlePreviewPdf}>
            <Eye size={14} /> Preview PDF
          </AppButton>
          {invoice.status === "draft" && (
            <AppButton size="sm" onClick={handleSend}>
              <Send size={14} /> Send Invoice
            </AppButton>
          )}
          {["draft", "sent", "viewed", "overdue"].includes(invoice.status) && (
            <AppButton variant="outline" size="sm" onClick={() => navigate(`/sa/service-invoices/edit/${id}`)}>
              <Edit size={14} /> Edit
            </AppButton>
          )}
          {invoice.status !== "paid" && invoice.status !== "cancelled" && (
            <AppButton variant="destructive" size="sm" onClick={handleCancel}>
              <XCircle size={14} /> Cancel
            </AppButton>
          )}
          {invoice.serviceType === "revenue_share" && invoice.status !== "paid" && invoice.status !== "cancelled" && (
            <AppButton variant="destructive" size="sm" onClick={handleVoidRevShare} disabled={voiding}>
              <RotateCcw size={14} /> {voiding ? "Voiding…" : "Void & Re-bill"}
            </AppButton>
          )}
          {invoice.serviceType === "revenue_share" && invoice.status === "paid" && (
            <AppButton variant="outline" size="sm" onClick={handleIssueCreditNote} disabled={issuingCN}>
              <Receipt size={14} /> {issuingCN ? "Issuing…" : "Credit Note"}
            </AppButton>
          )}
        </div>
      </div>

      {/* Payment Approval Section */}
      {invoice.payment?.approvalStatus === "pending" && (
        <div className="si-payment-review">
          <h3><Clock size={18} /> Manual Payment Pending Review</h3>
          <div className="si-payment-review-details">
            <DetailRow label="Transaction ID" value={invoice.payment.transactionId || "—"} />
            <DetailRow label="Submitted" value={formatDate(invoice.payment.submittedAt)} />
            {invoice.payment.proofFileName && (
              <DetailRow label="Proof File" value={invoice.payment.proofFileName} />
            )}
          </div>
          <div className="si-payment-review-actions">
            <AppButton size="sm" onClick={handleApprove}>
              <CheckCircle size={14} /> Approve Payment
            </AppButton>
            <AppButton variant="destructive" size="sm" onClick={handleReject}>
              <XCircle size={14} /> Reject Payment
            </AppButton>
          </div>
        </div>
      )}

      <div className="si-detail-grid">
        {/* Billing From (Company) */}
        {billingFrom.companyName && (
          <div className="si-detail-card">
            <h3><Building2 size={16} /> From (Seller)</h3>
            <DetailRow label="Company" value={billingFrom.companyName} />
            {billingFrom.address && <DetailRow label="Address" value={[billingFrom.address, billingFrom.city, billingFrom.state, billingFrom.pincode].filter(Boolean).join(", ")} />}
            {billingFrom.gstin && <DetailRow label="GSTIN" value={billingFrom.gstin} />}
            {billingFrom.pan && <DetailRow label="PAN" value={billingFrom.pan} />}
            {billingFrom.email && <DetailRow label="Email" value={billingFrom.email} />}
            {billingFrom.phone && <DetailRow label="Phone" value={billingFrom.phone} />}
          </div>
        )}

        {/* Bill To (Institute) */}
        <div className="si-detail-card">
          <h3><Building2 size={16} /> Bill To (Institute)</h3>
          <DetailRow label="Name" value={billingTo.name || inst.name || "—"} />
          <DetailRow label="Address" value={billingTo.address || inst.address || "—"} />
          <DetailRow label="Email" value={billingTo.email || inst.email || "—"} />
          <DetailRow label="Phone" value={billingTo.phone || inst.phone || "—"} />
          {(billingTo.gstin || inst.gstNumber) && <DetailRow label="GSTIN" value={billingTo.gstin || inst.gstNumber} />}
        </div>

        {/* Invoice Info */}
        <div className="si-detail-card">
          <h3><FileText size={16} /> Invoice Details</h3>
          <DetailRow label="Service Type" value={SERVICE_TYPE_LABELS[invoice.serviceType] || invoice.serviceType} />
          {invoice.billingType && <DetailRow label="Billing Type" value={{ "one-time": "One-time", recurring: "Recurring", milestone: "Milestone" }[invoice.billingType] || invoice.billingType} />}
          {invoice.sacCode && <DetailRow label="SAC Code" value={invoice.sacCode} />}
          <DetailRow label="Due Date" value={formatDate(invoice.dueDate)} />
          {invoice.placeOfSupply && <DetailRow label="Place of Supply" value={invoice.placeOfSupply} />}
          <DetailRow label="Reverse Charge" value={invoice.reverseCharge ? "Yes" : "No"} />
          <DetailRow label="Subtotal" value={formatCurrency(invoice.subtotal || invoice.totalAmount)} />
          {isIntra && (
            <>
              <DetailRow label={`CGST @ ${invoice.cgstRate}%`} value={formatCurrency(invoice.cgstAmount)} />
              <DetailRow label={`SGST @ ${invoice.sgstRate}%`} value={formatCurrency(invoice.sgstAmount)} />
            </>
          )}
          {isInter && <DetailRow label={`IGST @ ${invoice.igstRate}%`} value={formatCurrency(invoice.igstAmount)} />}
          {hasTax && <DetailRow label="Total GST" value={formatCurrency(invoice.taxAmount)} />}
          {invoice.roundedOff !== 0 && invoice.roundedOff != null && <DetailRow label="Rounded Off" value={`${invoice.roundedOff > 0 ? "+" : ""}${formatCurrency(Math.abs(invoice.roundedOff))}`} />}
          <DetailRow label="Total Amount" value={formatCurrency(invoice.totalAmount)} />
          {invoice.sentAt && <DetailRow label="Sent At" value={formatDate(invoice.sentAt)} />}
          {invoice.viewedAt && <DetailRow label="Viewed At" value={formatDate(invoice.viewedAt)} />}
          {invoice.serviceRequestId && <DetailRow label="Service Request" value={invoice.serviceRequestId} />}
        </div>
      </div>

      {/* Line Items */}
      <div className="si-detail-card si-line-items-detail">
        <h3>Line Items</h3>
        <table className="si-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Description</th>
              <th>Qty</th>
              <th>Unit Price</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lineItems?.map((item, idx) => (
              <tr key={item._id || idx}>
                <td>{idx + 1}</td>
                <td>{item.description}</td>
                <td>{item.quantity}</td>
                <td>{formatCurrency(item.unitPrice)}</td>
                <td>{formatCurrency(item.amount)}</td>
              </tr>
            ))}
            <tr className="si-total-tr">
              <td colSpan={4} className="si-total-label">Subtotal</td>
              <td className="si-total-value">{formatCurrency(invoice.subtotal || invoice.totalAmount)}</td>
            </tr>
            {isIntra && (
              <>
                <tr className="si-total-tr si-gst-tr">
                  <td colSpan={4} className="si-total-label">CGST @ {invoice.cgstRate}%</td>
                  <td className="si-total-value">{formatCurrency(invoice.cgstAmount)}</td>
                </tr>
                <tr className="si-total-tr si-gst-tr">
                  <td colSpan={4} className="si-total-label">SGST @ {invoice.sgstRate}%</td>
                  <td className="si-total-value">{formatCurrency(invoice.sgstAmount)}</td>
                </tr>
              </>
            )}
            {isInter && (
              <tr className="si-total-tr si-gst-tr">
                <td colSpan={4} className="si-total-label">IGST @ {invoice.igstRate}%</td>
                <td className="si-total-value">{formatCurrency(invoice.igstAmount)}</td>
              </tr>
            )}
            {invoice.roundedOff !== 0 && invoice.roundedOff != null && (
              <tr className="si-total-tr si-gst-tr">
                <td colSpan={4} className="si-total-label">Rounded Off</td>
                <td className="si-total-value">{invoice.roundedOff > 0 ? "+" : ""}{formatCurrency(Math.abs(invoice.roundedOff))}</td>
              </tr>
            )}
            <tr className="si-total-tr si-grand-total-tr">
              <td colSpan={4} className="si-total-label">Total Invoice Amount</td>
              <td className="si-total-value">{formatCurrency(invoice.totalAmount)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Revenue Share Drilldown */}
      {invoice.serviceType === "revenue_share" && (
        <>
          <div className="si-detail-card">
            <h3><IndianRupee size={16} /> Revenue Share Summary</h3>
            <div className="si-detail-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.75rem" }}>
              {invoice.revenueShare?.period && <DetailRow label="Period" value={invoice.revenueShare.period} />}
              {invoice.revenueShare?.studentCount != null && <DetailRow label="Students Billed" value={invoice.revenueShare.studentCount} />}
              {invoice.revenueShare?.batchStudentCount != null && <DetailRow label="Batch Students" value={invoice.revenueShare.batchStudentCount} />}
              {invoice.revenueShare?.selfPacedStudentCount != null && <DetailRow label="Self-paced Students" value={invoice.revenueShare.selfPacedStudentCount} />}
              {invoice.revenueShare?.commissionRate != null && <DetailRow label="Commission Rate" value={`${invoice.revenueShare.commissionRate}%`} />}
              {invoice.revenueShare?.commissionRateSource && <DetailRow label="Rate Source" value={invoice.revenueShare.commissionRateSource === "institute_override" ? "Override" : "Plan default"} />}
              {invoice.revenueShare?.courseFeesTotal != null && <DetailRow label="Course Fees Total" value={formatCurrency(invoice.revenueShare.courseFeesTotal)} />}
              {invoice.revenueShare?.cashFlow && (
                <>
                  <DetailRow label="Collected Online" value={formatCurrency(invoice.revenueShare.cashFlow.collectedOnline)} />
                  <DetailRow label="Collected Manual" value={formatCurrency(invoice.revenueShare.cashFlow.collectedManual)} />
                  <DetailRow label="Outstanding" value={formatCurrency(invoice.revenueShare.cashFlow.outstanding)} />
                </>
              )}
            </div>
          </div>

          <div className="si-detail-card si-line-items-detail">
            <h3><FileText size={16} /> Per-Student Breakdown ({revShareLines.length})</h3>
            {revShareLoading ? (
              <div className="text-center py-3"><div className="spinner-border spinner-border-sm" /></div>
            ) : revShareLines.length === 0 ? (
              <div className="text-muted text-center py-3">No line records found.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="si-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Course</th>
                      <th>Source</th>
                      <th>Activated</th>
                      <th>Days</th>
                      <th>Course Fee</th>
                      <th>Bill</th>
                      <th>Online</th>
                      <th>Manual</th>
                      <th>Outstanding</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revShareLines.map((l) => (
                      <tr key={l._id}>
                        <td>
                          <div>{l.studentName || l.studentId?.name || "—"}</div>
                          <small className="text-muted">{l.studentEmail || l.studentId?.email}</small>
                        </td>
                        <td>
                          {l.courseName || l.courseId?.name || "—"}
                          {l.batchName && <div><small className="text-muted">{l.batchName}</small></div>}
                        </td>
                        <td><AppBadge variant={l.sourceType === "batch" ? "info" : "primary"}>{l.sourceType}</AppBadge></td>
                        <td>{l.activatedAt ? new Date(l.activatedAt).toLocaleDateString("en-IN") : "—"}</td>
                        <td>{l.daysActiveAtBilling}</td>
                        <td>{formatCurrency(l.coursePrice)}</td>
                        <td><strong>{formatCurrency(l.billAmount)}</strong></td>
                        <td>{formatCurrency(l.collectedOnline || 0)}</td>
                        <td>{formatCurrency(l.collectedManual || 0)}</td>
                        <td>{formatCurrency(l.outstanding || 0)}</td>
                        <td><AppBadge variant={l.status === "voided" ? "secondary" : "success"}>{l.status}</AppBadge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Notes */}
      {invoice.notes && (
        <div className="si-detail-card">
          <h3>Notes</h3>
          <p className="si-notes-text">{invoice.notes}</p>
        </div>
      )}

      {/* Payment Info (if paid) */}
      {invoice.status === "paid" && invoice.payment && (
        <div className="si-detail-card si-paid-card">
          <h3><CheckCircle size={16} /> Payment Information</h3>
          <DetailRow label="Method" value={invoice.payment.method || "—"} />
          {invoice.payment.transactionId && <DetailRow label="Transaction ID" value={invoice.payment.transactionId} />}
          {invoice.payment.paidAt && <DetailRow label="Paid At" value={formatDate(invoice.payment.paidAt)} />}
        </div>
      )}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ show: false, message: "", type: "success" })}
      />
    </div>
  );
};

export default ServiceInvoiceDetail;
